
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";

interface NotificationSettings {
  timeline_entries_email: boolean;
  timeline_entries_app: boolean;
  friend_requests_email: boolean;
  friend_requests_app: boolean;
  birthdays_email: boolean;
  birthdays_app: boolean;
}

export const useNotificationSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<NotificationSettings | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchSettings = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('notification_settings')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      if (data) {
        setSettings({
          timeline_entries_email: data.timeline_entries_email,
          timeline_entries_app: data.timeline_entries_app,
          friend_requests_email: data.friend_requests_email,
          friend_requests_app: data.friend_requests_app,
          birthdays_email: data.birthdays_email,
          birthdays_app: data.birthdays_app,
        });
      } else {
        // Create default settings if none exist
        const { data: newSettings, error: createError } = await supabase
          .from('notification_settings')
          .insert({
            user_id: user.id,
            timeline_entries_email: true,
            timeline_entries_app: true,
            friend_requests_email: true,
            friend_requests_app: true,
            birthdays_email: true,
            birthdays_app: true,
          })
          .select()
          .single();

        if (createError) throw createError;
        
        if (newSettings) {
          setSettings({
            timeline_entries_email: newSettings.timeline_entries_email,
            timeline_entries_app: newSettings.timeline_entries_app,
            friend_requests_email: newSettings.friend_requests_email,
            friend_requests_app: newSettings.friend_requests_app,
            birthdays_email: newSettings.birthdays_email,
            birthdays_app: newSettings.birthdays_app,
          });
        }
      }
    } catch (error) {
      console.error('Error fetching notification settings:', error);
      toast({
        title: "Fehler",
        description: "Benachrichtigungseinstellungen konnten nicht geladen werden.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const updateSettings = async (updates: Partial<NotificationSettings>) => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('notification_settings')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id);

      if (error) throw error;

      setSettings(prev => prev ? { ...prev, ...updates } : null);
      
      toast({
        title: "Erfolgreich",
        description: "Benachrichtigungseinstellungen wurden aktualisiert.",
      });

      return true;
    } catch (error) {
      console.error('Error updating notification settings:', error);
      toast({
        title: "Fehler",
        description: "Einstellungen konnten nicht aktualisiert werden.",
        variant: "destructive"
      });
      return false;
    }
  };

  useEffect(() => {
    fetchSettings();
  }, [user]);

  return {
    settings,
    loading,
    updateSettings,
    refreshSettings: fetchSettings
  };
};
